import React, { useState, useEffect, useCallback, useMemo } from "react"
import { useNavigate } from "@tanstack/react-router"
import { AuthContext, type AuthContextType, type SignupPayload, type UpdateProfilePayload } from "./AuthContext"
import { getLoginRedirectPath, type LoginRedirectHint } from "./authRedirect"
import type { User } from "@/types"
import {
  getCurrentUser,
  login as loginSDK,
  logout as logoutSDK,
  signup as signupSDK,
  updateUser,
} from "@/client/sdk.gen"
import { ApiError } from "@/client/config"
import type { SignupRequestWritable, UserResponse } from "@/client"
import { getUserDisplayName } from "@/utils/user"
import { setAuthRoutePrefetchState } from "@/utils/routePrefetch"
import { saveUserSnapshot, loadUserSnapshot, clearUserSnapshot, onGlobal401 } from "@/utils/userSessionCache"
import { queryClient } from "@/queryClient"

/**
 * Props accepted by the AuthProvider wrapper.
 */
interface AuthProviderProps {
  children: React.ReactNode
}

/**
 * Maps an API user response to the internal User type with validation.
 *
 * @param payload - User data returned from the API.
 * @throws Error when the payload is missing required identifiers.
 * @returns A normalized User object.
 */
const mapToUser = (payload: UserResponse): User => {
  const email = payload.email ?? ""
  const name = getUserDisplayName(payload)

  return {
    id: payload.id,
    email,
    name,
    givenName: payload.given_name,
    familyName: payload.family_name,
    locale: payload.locale,
    role: payload.role,
    type: payload.type,
  }
}

/**
 * Provides authentication state and actions to the application tree.
 *
 * @param children - Components that should have access to authentication context.
 * @returns Context provider that manages login, logout, signup, and session refresh.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const [isBackendMode, setIsBackendMode] = useState(false)
  const navigate = useNavigate()

  /**
   * Refreshes the current user session by calling the backend and normalizing the response.
   */
  const refreshUser = useCallback(async (): Promise<User | null> => {
    try {
      const { data } = await getCurrentUser({ throwOnError: true })
      const normalizedUser = mapToUser(data)
      setUser(normalizedUser)
      setIsBackendMode(true)
      setAuthRoutePrefetchState({ isBackendMode: true, isInitialized: true })
      saveUserSnapshot(normalizedUser)
      return normalizedUser
    } catch (error) {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403 || error.status === 404)) {
        setUser(null)
        setIsBackendMode(false)
        setAuthRoutePrefetchState({ isBackendMode: false, isInitialized: true })
        clearUserSnapshot()
        return null
      }
      console.error("Failed to refresh current user", error)
      setIsBackendMode(false)
      setAuthRoutePrefetchState({ isBackendMode: false, isInitialized: true })
      throw error
    }
  }, [])

  useEffect(() => {
    let isMounted = true

    const cachedUser = loadUserSnapshot()
    if (cachedUser) {
      setUser(cachedUser)
      setIsBackendMode(true)
      setAuthRoutePrefetchState({ isBackendMode: true, isInitialized: true })
      setIsInitializing(false)
      ;(async () => {
        try {
          await refreshUser()
        } catch (error) {
          if (!(error instanceof ApiError && (error.status === 401 || error.status === 403))) {
            console.error("Unable to refresh cached authentication", error)
          }
        }
      })()

      return
    }

    ;(async () => {
      try {
        await refreshUser()
      } catch (error) {
        if (!(error instanceof ApiError && (error.status === 401 || error.status === 403))) {
          console.error("Unable to initialize authentication", error)
        }
      } finally {
        if (isMounted) {
          setIsInitializing(false)
        }
      }
    })()

    return () => {
      isMounted = false
    }
  }, [refreshUser])

  // Invalidate auth state on any 401 (e.g. session revoked in another tab).
  // Does NOT navigate to /login so demo mode keeps working.
  useEffect(() => {
    return onGlobal401(() => {
      queryClient.clear()
      setUser(null)
      setIsBackendMode(false)
      setAuthRoutePrefetchState({ isBackendMode: false, isInitialized: true })
    })
  }, [])

  /**
   * Redirects users to the correct dashboard after successful authentication.
   */
  const navigateAfterLogin = useCallback(
    (nextUser: User, redirectHint?: LoginRedirectHint, redirectPath?: string) => {
      const destination = getLoginRedirectPath(nextUser, redirectHint, redirectPath)
      navigate({ to: destination })
    },
    [navigate],
  )

  /**
   * Authenticates the user with the backend and stores the normalized session.
   */
  const login = useCallback(
    async (
      email: string,
      password: string,
      options?: { redirectHint?: LoginRedirectHint; redirectPath?: string },
    ): Promise<User> => {
      const { data } = await loginSDK({
        body: { email, password },
        throwOnError: true,
      })
      const normalizedUser = mapToUser(data)
      queryClient.clear()
      setUser(normalizedUser)
      setIsBackendMode(true)
      setAuthRoutePrefetchState({ isBackendMode: true, isInitialized: true })
      saveUserSnapshot(normalizedUser)
      navigateAfterLogin(normalizedUser, options?.redirectHint, options?.redirectPath)
      return normalizedUser
    },
    [navigateAfterLogin],
  )

  /**
   * Registers a new account using the provided signup payload.
   */
  const signup = useCallback(async (payload: SignupPayload): Promise<void> => {
    const requestBody: SignupRequestWritable = {
      email: payload.email,
      password: payload.password,
      fullName: payload.fullName,
      givenName: payload.givenName,
      familyName: payload.familyName,
      locale: payload.locale,
    }

    await signupSDK({
      body: requestBody,
      throwOnError: true,
    })
  }, [])

  /**
   * Updates the current user's profile details and keeps local auth state in sync.
   */
  const updateProfile = useCallback(
    async (payload: UpdateProfilePayload): Promise<User> => {
      if (!user) {
        throw new Error("You must be signed in to update your profile")
      }

      const { data } = await updateUser({
        path: { user_id: user.id },
        body: {
          name: payload.name.trim(),
        },
        throwOnError: true,
      })

      const normalizedUser = mapToUser(data)
      setUser(normalizedUser)
      saveUserSnapshot(normalizedUser)
      return normalizedUser
    },
    [user],
  )

  /**
   * Logs the user out and clears session state regardless of API errors.
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      await logoutSDK({ throwOnError: true })
    } catch (error) {
      if (error instanceof ApiError && !(error.status === 401 || error.status === 403)) {
        console.error("Failed to log out", error)
      }
    } finally {
      queryClient.clear()
      setUser(null)
      setIsBackendMode(false)
      setAuthRoutePrefetchState({ isBackendMode: false, isInitialized: true })
      clearUserSnapshot()
      navigate({ to: "/login" })
    }
  }, [navigate])

  const value: AuthContextType = useMemo(
    () => ({
      user,
      isAuthenticated: !!user,
      isInitializing,
      isBackendMode,
      login,
      logout,
      signup,
      updateProfile,
      refreshUser,
    }),
    [user, isInitializing, isBackendMode, login, logout, signup, updateProfile, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
